import React from 'react';
import styled from 'styled-components';
import DSBottomSheet from '../../../components/designSystem/DSBottomSheet';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { convertToVwMob } from '../../../Utils/responsive';
import mainLogo from '../../../assets/Logo/main_logo.png';
import { useSignup } from '../useSignup';
import { useTranslation } from 'react-i18next';

const SignupMob = () => {
  const { t } = useTranslation('translation');
  const emailInputRef = React.useRef<HTMLInputElement>(null);
  const passwordInputRef = React.useRef<HTMLInputElement>(null);
  const step4ContainerRef = React.useRef<HTMLDivElement>(null);
  const [isInputFocused, setIsInputFocused] = React.useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = React.useState(false);
  const [isStep3InputFocused, setIsStep3InputFocused] = React.useState(false);
  const [step4NeedsScroll, setStep4NeedsScroll] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = React.useState(false);
  const {
    step,
    fundraiserType,
    setFundraiserType,
    email,
    setEmail,
    password,
    setPassword,
    passwordConfirm,
    setPasswordConfirm,
    name,
    setName,
    isNameLocked,
    phoneNumber,
    setPhoneNumber,
    isPhoneLocked,
    category,
    setCategory,
    handleNext,
    handlePrev,
    handleConfirm,
    isSubmitting,
    error,
    emailCheckStatus,
    emailCheckMessage,
    isEmailAvailable,
    passwordMessage,
    goToLogin,
    goToAccount,
    goToDashboard,
  } = useSignup();

  React.useEffect(() => {
    document.querySelector<HTMLElement>('[data-mobile-scroll-main]')?.scrollTo({ top: 0, behavior: 'auto' });
    if (step === 1 && emailInputRef.current) {
      emailInputRef.current.focus();
    }
    if (step === 2 && passwordInputRef.current) {
      passwordInputRef.current.focus();
    }
  }, [step]);

  const updateStep4ScrollState = React.useCallback(() => {
    const container = step4ContainerRef.current;
    if (!container) return;
    const needsScroll = container.scrollHeight > container.clientHeight + 1;
    setStep4NeedsScroll(needsScroll);
  }, []);

  React.useEffect(() => {
    if (step !== 4) return;

    const rid = requestAnimationFrame(updateStep4ScrollState);
    const tid = window.setTimeout(updateStep4ScrollState, 60);
    const onResize = () => updateStep4ScrollState();
    window.addEventListener('resize', onResize);
    window.visualViewport?.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(rid);
      window.clearTimeout(tid);
      window.removeEventListener('resize', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
  }, [step, email, name, updateStep4ScrollState]);

  const renderStep0 = () => (
    <>
      <TopBar>
        <BackButton onClick={handlePrev}>
          <ArrowIcon>←</ArrowIcon>
          {t('common.actions.back')}
        </BackButton>
      </TopBar>

      <Step0ContentWrapper>
        <Title style={{ marginTop: 0 }}>{t('page.signup.step0.title')}</Title>

        <RadioGroup style={{ paddingLeft: convertToVwMob(20) }}>
          <RadioOption active={fundraiserType === 'individual'} onClick={() => setFundraiserType('individual')}>
            <RadioCircle active={fundraiserType === 'individual'} />
            <RadioLabel active={fundraiserType === 'individual'}>{t('page.signup.step0.types.individual')}</RadioLabel>
          </RadioOption>
          <RadioOption active={fundraiserType === 'group'} onClick={() => setFundraiserType('group')}>
            <RadioCircle active={fundraiserType === 'group'} />
            <RadioLabel active={fundraiserType === 'group'}>{t('page.signup.step0.types.group')}</RadioLabel>
          </RadioOption>
        </RadioGroup>
      </Step0ContentWrapper>

      <KeyboardButtonWrapper isKeyboardVisible={false}>
        <NextButton onClick={handleNext}>{t('common.actions.next')}</NextButton>
      </KeyboardButtonWrapper>
    </>
  );

  const renderStep1 = () => (
    <>
      <LogoSection>
        <Logo src={mainLogo} alt="Mission Fund" />
      </LogoSection>

      <Step1ContentWrapper>
        <Stepper activeStep={1} />
        <Step1InputSection>
          <Title style={{ marginTop: 0, marginBottom: convertToVwMob(16) }}>
            {t('page.signup.step1.titleLine1')}<br />{t('page.signup.step1.titleLine2')}
          </Title>

          <InputGroup>
            <InputWithClear>
              <Input
                ref={emailInputRef}
                type="email"
                $withActions={Boolean(email)}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setIsInputFocused(true)}
                onBlur={() => setIsInputFocused(false)}
                placeholder={t('page.signup.step1.emailPlaceholder')}
              />
              {email && (
                <ClearButton onClick={() => setEmail('')}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#BCBCBC"/>
                    <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ClearButton>
              )}
            </InputWithClear>
            <HelperText>{t('page.signup.step1.helperLine1')}<br /> {t('page.signup.step1.helperLine2')}</HelperText>
            {emailCheckStatus !== 'idle' && emailCheckMessage && (
              <EmailCheckText $status={emailCheckStatus}>
                {emailCheckStatus === 'checking' && <EmailCheckSpinner />}
                {emailCheckMessage}
              </EmailCheckText>
            )}
          </InputGroup>
        </Step1InputSection>
      </Step1ContentWrapper>

      <KeyboardButtonWrapper isKeyboardVisible={isInputFocused}>
        <NextButton
          onClick={handleNext}
          disabled={!email || emailCheckStatus === 'checking' || !isEmailAvailable}
          isKeyboardMode={isInputFocused}
        >
          {t('common.actions.next')}
        </NextButton>
      </KeyboardButtonWrapper>
    </>
  );

  const renderStep2 = () => (
    <>
      <LogoSection>
        <Logo src={mainLogo} alt="Mission Fund" />
      </LogoSection>

      <Step2ContentWrapper>
        <Stepper activeStep={2} />
        <Title style={{ marginTop: 0, marginBottom: 0 }}>
          {t('page.signup.step2.titleLine1')}<br />{t('page.signup.step2.titleLine2')}
        </Title>
        <Step2InputSection>
          <EmailDisplayBox>
            <InfoLabel>{t('page.signup.step2.accountLabel')}</InfoLabel>
            <InfoValue>{email}</InfoValue>
          </EmailDisplayBox>

          <PasswordsGroup>
            <PasswordInputWrapper>
            <InputWithClear>
              <Input
                ref={passwordInputRef}
                type={showPassword ? 'text' : 'password'}
                $withActions={Boolean(password)}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
                placeholder={t('page.signup.step2.passwordPlaceholder')}
              />
              {password && (
                <PasswordActionsWrapper>
                  <PasswordToggleButton onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z" fill="#929292"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 23 12C21.27 7.61 17 4.5 12 4.5C10.6 4.5 9.26 4.75 8.04 5.2L10.17 7.33C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.74 7.01C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3M12 17C9.24 17 7 14.76 7 12C7 11.18 7.19 10.42 7.54 9.73L8.92 11.11C8.86 11.4 8.83 11.69 8.83 12C8.83 13.93 10.4 15.5 12.33 15.5C12.64 15.5 12.93 15.47 13.22 15.41L14.6 16.79C13.91 16.93 13.18 17 12 17ZM14.97 11.17C14.82 9.77 13.72 8.68 12.33 8.53L14.97 11.17Z" fill="#929292"/>
                      </svg>
                    )}
                  </PasswordToggleButton>
                  <ClearButton onClick={() => setPassword('')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#BCBCBC"/>
                      <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </ClearButton>
                </PasswordActionsWrapper>
              )}
            </InputWithClear>
          </PasswordInputWrapper>

          <PasswordInputWrapper>
            <InputWithClear>
              <Input
                type={showPasswordConfirm ? 'text' : 'password'}
                $withActions={Boolean(passwordConfirm)}
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                onFocus={() => setIsPasswordFocused(true)}
                onBlur={() => setIsPasswordFocused(false)}
                placeholder={t('page.signup.step2.passwordConfirmPlaceholder')}
              />
              {passwordConfirm && (
                <PasswordActionsWrapper>
                  <PasswordToggleButton onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}>
                    {showPasswordConfirm ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5C7 5 2.73 8.11 1 12.5C2.73 16.89 7 20 12 20C17 20 21.27 16.89 23 12.5C21.27 8.11 17 5 12 5ZM12 17.5C9.24 17.5 7 15.26 7 12.5C7 9.74 9.24 7.5 12 7.5C14.76 7.5 17 9.74 17 12.5C17 15.26 14.76 17.5 12 17.5ZM12 9.5C10.34 9.5 9 10.84 9 12.5C9 14.16 10.34 15.5 12 15.5C13.66 15.5 15 14.16 15 12.5C15 10.84 13.66 9.5 12 9.5Z" fill="#929292"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 23 12C21.27 7.61 17 4.5 12 4.5C10.6 4.5 9.26 4.75 8.04 5.2L10.17 7.33C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.74 7.01C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3M12 17C9.24 17 7 14.76 7 12C7 11.18 7.19 10.42 7.54 9.73L8.92 11.11C8.86 11.4 8.83 11.69 8.83 12C8.83 13.93 10.4 15.5 12.33 15.5C12.64 15.5 12.93 15.47 13.22 15.41L14.6 16.79C13.91 16.93 13.18 17 12 17ZM14.97 11.17C14.82 9.77 13.72 8.68 12.33 8.53L14.97 11.17Z" fill="#929292"/>
                      </svg>
                    )}
                  </PasswordToggleButton>
                  <ClearButton onClick={() => setPasswordConfirm('')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#BCBCBC"/>
                      <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </ClearButton>
                </PasswordActionsWrapper>
              )}
            </InputWithClear>
            {passwordMessage && <PasswordErrorText>{passwordMessage}</PasswordErrorText>}
          </PasswordInputWrapper>
          </PasswordsGroup>
        </Step2InputSection>
      </Step2ContentWrapper>

      <KeyboardButtonWrapper isKeyboardVisible={isPasswordFocused}>
        <NextButton onClick={handleNext} disabled={!password || password !== passwordConfirm} isKeyboardMode={isPasswordFocused}>{t('common.actions.next')}</NextButton>
      </KeyboardButtonWrapper>
    </>
  );

  const [isSelectOpen, setIsSelectOpen] = React.useState(false);

  const renderStep3 = () => {
    const individualOptions = [
      { value: 'Pastor', label: t('page.signup.step3.categories.churchWorker') },
      { value: 'Missionary', label: t('page.signup.step3.categories.missionary') },
      { value: 'Staff', label: t('page.signup.step3.categories.organizationWorker') },
      { value: 'Private', label: t('page.signup.step3.categories.individualEtc') },
    ];

    const groupOptions = [
      { value: 'NGO', label: t('page.signup.step3.categories.nonProfit') },
      { value: 'Church', label: t('page.myAccount.userType.church') },
      { value: 'Orphanage', label: t('page.signup.step3.categories.orphanage') },
      { value: 'SmallGroup', label: t('page.signup.step3.categories.prison') },
      { value: 'MissionSchool', label: t('page.signup.step3.categories.school') },
      { value: 'ETC', label: t('page.signup.step3.categories.groupEtc') },
    ];

    const currentOptions = fundraiserType === 'individual' ? individualOptions : groupOptions;
    const selectedLabel = currentOptions.find(opt => opt.value === category)?.label || t('page.signup.step3.categoryPlaceholder');

    return (
      <>
        <LogoSection>
          <Logo src={mainLogo} alt="Mission Fund" />
        </LogoSection>

        <Step3ContentWrapper>
          <Stepper activeStep={3} />
          <Title style={{ marginTop: 0, marginBottom: 0 }}>
            {fundraiserType === 'individual' ? t('page.signup.step3.titleIndividual') : t('page.signup.step3.titleGroup')}
          </Title>
          <Step3InputSection>
            <InputWithClear>
              <Input
                type="text"
                $withActions={Boolean(name) && !isNameLocked}
                value={name}
                onChange={(e) => setName(e.target.value)}
                readOnly={isNameLocked}
                onFocus={() => setIsStep3InputFocused(true)}
                onBlur={() => setIsStep3InputFocused(false)}
                placeholder={
                  fundraiserType === 'individual'
                    ? t('page.signup.step3.namePlaceholderIndividual')
                    : t('page.signup.step3.namePlaceholderGroup')
                }
              />
              {name && !isNameLocked && (
                <ClearButton onClick={() => setName('')}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#BCBCBC"/>
                    <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </ClearButton>
              )}
            </InputWithClear>

            <InputGroup>
              <InputWithClear>
                <Input
                  type="tel"
                  $withActions={Boolean(phoneNumber) && !isPhoneLocked}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))}
                  readOnly={isPhoneLocked}
                  onFocus={() => setIsStep3InputFocused(true)}
                  onBlur={() => setIsStep3InputFocused(false)}
                  placeholder={
                    fundraiserType === 'individual'
                      ? t('page.signup.step3.phonePlaceholderIndividual')
                      : t('page.signup.step3.phonePlaceholderGroup')
                  }
                />
                {phoneNumber && !isPhoneLocked && (
                  <ClearButton onClick={() => setPhoneNumber('')}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" fill="#BCBCBC"/>
                      <path d="M8 8L16 16M16 8L8 16" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  </ClearButton>
                )}
              </InputWithClear>
              <HelperText style={{marginTop: '-4px'}}>{t('page.signup.step3.notes.contact')}</HelperText>
              {(isNameLocked || isPhoneLocked) && (
                <HelperText style={{marginTop: '-4px'}}>{t('page.signup.step3.notes.snsLocked')}</HelperText>
              )}
            </InputGroup>

            <SelectFieldWrapper onClick={() => setIsSelectOpen(true)}>
              <SelectFieldLabel hasValue={!!category}>
                {selectedLabel}
              </SelectFieldLabel>
              <ChevronIcon isOpen={false} />
            </SelectFieldWrapper>
          </Step3InputSection>

          {error && <ErrorText>{error}</ErrorText>}
        </Step3ContentWrapper>

        <KeyboardButtonWrapper isKeyboardVisible={isStep3InputFocused}>
          <NextButton
            onClick={handleConfirm}
            disabled={isSubmitting || !name || !phoneNumber || !category}
            isKeyboardMode={isStep3InputFocused}
          >
            {isSubmitting ? t('common.actions.processing') : t('common.actions.next')}
          </NextButton>
        </KeyboardButtonWrapper>

        {isSelectOpen && (
          <DSBottomSheet
            open={isSelectOpen}
            title={t('page.signup.step3.categoryModalTitle')}
            onClose={() => setIsSelectOpen(false)}
          >
            <StyledBottomSheetContent>
              {currentOptions.map((opt) => (
                <RadioOptionItem
                  key={opt.value}
                  active={category === opt.value}
                  onClick={() => setCategory(opt.value)}
                >
                  <RadioCircle active={category === opt.value} />
                  <RadioLabel active={category === opt.value}>{opt.label}</RadioLabel>
                </RadioOptionItem>
              ))}
            </StyledBottomSheetContent>
            <BottomSheetButton
              onClick={() => setIsSelectOpen(false)}
              disabled={!category}
            >
              {t('common.actions.selectComplete')}
            </BottomSheetButton>
          </DSBottomSheet>
        )}
      </>
    );
  };

  const renderStep4 = () => (
    <>
      <Step4Container ref={step4ContainerRef} $enableScroll={step4NeedsScroll}>
        <Step4LogoSection>
          <WelcomeLogo src={mainLogo} alt="Mission Fund" />
        </Step4LogoSection>

        <Step4Content>
          <WelcomeTitle>{t('page.signup.step4.title')}</WelcomeTitle>
          <WelcomeSubText>
            {t('page.signup.step4.messageMobLine1')}
            <br />
            {t('page.signup.step4.messageMobLine2')}
          </WelcomeSubText>

          <WelcomeProfileSection>
            <WelcomeProfileCircle>
              <svg width={convertToVwMob(62)} height={convertToVwMob(62)} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="#2BC2E1" />
              </svg>
            </WelcomeProfileCircle>
            <WelcomeProfileInfo>
              <WelcomeProfileEmail>{email}</WelcomeProfileEmail>
              <WelcomeProfileName>{name}</WelcomeProfileName>
            </WelcomeProfileInfo>
          </WelcomeProfileSection>
        </Step4Content>

        <Step4ButtonWrapper>
          <WelcomeButton type="button" onClick={goToAccount}>
            {t('common.actions.start')}
          </WelcomeButton>
          <LaterButton type="button" onClick={goToDashboard}>
            {t('common.actions.skipLater')}
          </LaterButton>
        </Step4ButtonWrapper>
      </Step4Container>
    </>
  );

  return (
    <MobilePageLayout title="" hideHeader hideFooter showBottomNav={false}>
      <BackgroundOverlay />
      <Container>
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
        {step === 4 && renderStep4()}
      </Container>
    </MobilePageLayout>
  );
};

const Stepper = ({ activeStep }: { activeStep: number }) => (
  <StepperWrapper>
    <StepLine />
    {/* 비밀번호 단계(2)가 없으므로 이메일(1) → 추가정보(3) 두 단계만 표시한다 */}
    <StepCircle active={activeStep === 1}>1</StepCircle>
    <StepCircle active={activeStep === 3}>2</StepCircle>
  </StepperWrapper>
);

const StepperWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(44)};
`;

const StepLine = styled.div`
  position: absolute;
  left: ${convertToVwMob(15)};
  width: ${convertToVwMob(62)};
  height: ${convertToVwMob(3)};
  background: #F0F1F2;
  top: 50%;
  transform: translateY(-50%);
  z-index: 0;
`;

const StepCircle = styled.div<{ active: boolean }>`
  width: ${({ active }) => (active ? convertToVwMob(30) : convertToVwMob(20))};
  height: ${({ active }) => (active ? convertToVwMob(30) : convertToVwMob(20))};
  border-radius: 50%;
  background: ${({ active }) => (active ? '#2BC2E1' : '#F0F1F2')};
  color: white;
  font-size: ${({ active }) => (active ? convertToVwMob(20) : convertToVwMob(12))};
  font-weight: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  position: relative;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  position: relative;
`;

const BackgroundOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.5;
  z-index: -1;
  pointer-events: none;
`;

const TopBar = styled.div`
  width: 100%;
  padding: ${convertToVwMob(16)} 0;
  display: flex;
  justify-content: flex-start;
`;

const BackButton = styled.button`
  background: none;
  border: 2px solid #BCBCBC;
  border-radius: ${convertToVwMob(100)};
  padding: ${convertToVwMob(10)} ${convertToVwMob(20)};
  color: #2BC2E1;
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
`;

const ArrowIcon = styled.span`
  font-size: ${convertToVwMob(20)};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LogoSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  padding: ${convertToVwMob(10)} 0;
`;

const Logo = styled.img`
  width: ${convertToVwMob(155)};
  height: ${convertToVwMob(40)};
  object-fit: contain;
`;


const Step0ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(30)};
  padding-top: ${convertToVwMob(34)};
  padding-bottom: ${convertToVwMob(44)};
  flex: 1;
  overflow-y: auto;
`;

const Step1ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(30)};
  padding-top: ${convertToVwMob(34)};
  padding-bottom: ${convertToVwMob(44)};
  flex: 1;
  overflow-y: auto;
`;

const Step1InputSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(16)};
`;

const Step2ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(30)};
  padding-top: ${convertToVwMob(34)};
  padding-bottom: ${convertToVwMob(44)};
  flex: 1;
  overflow-y: auto;
`;

const Step2InputSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
`;

const Step3ContentWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(30)};
  padding-top: ${convertToVwMob(34)};
  padding-bottom: ${convertToVwMob(110)};
  flex: 1;
  overflow-y: auto;
`;

const Step3InputSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
`;

const PasswordInputWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const PasswordsGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
`;

const InputGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(8)};
`;

const InputWithClear = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #BCBCBC;

  &:focus-within {
    border-bottom: 1px solid #2BC2E1;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  flex-shrink: 0;
`;

const PasswordActionsWrapper = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
`;

const PasswordToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  flex-shrink: 0;
`;

const EmailDisplayBox = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(4)};
`;

const Title = styled.div`
  font-size: ${convertToVwMob(24)};
  font-weight: 400;
  color: #17171B;
  margin: 0;
  text-align: left;
  line-height: 1.5;
`;

const RadioGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
  margin: 0;
`;

const RadioOption = styled.div<{ active: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(16)};
  cursor: pointer;
`;

const RadioCircle = styled.div<{ active: boolean }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  min-width: ${convertToVwMob(20)};
  border-radius: 50%;
  border: ${convertToVwMob(1.5)} solid #E5E5E5;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(10)};
    height: ${convertToVwMob(10)};
    border-radius: 50%;
    background: #2BC2E1;
    transform: ${({ active }) =>
      active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const RadioLabel = styled.span<{ active: boolean }>`
  font-size: ${convertToVwMob(20)};
  color: ${({ active }) => (active ? '#17171B' : '#929292')};
  font-weight: ${({ active }) => (active ? 500 : 400)};
  line-height: 1.5;
`;


const Input = styled.input<{ $withActions?: boolean }>`
  width: 100%;
  padding: ${convertToVwMob(8)} 0;
  padding-right: ${({ $withActions }) => ($withActions ? convertToVwMob(60) : convertToVwMob(8))};
  border: none;
  font-size: ${convertToVwMob(20)};
  color: #17171B;
  outline: none;
  background: transparent;

  &::placeholder {
    color: #676767;
  }
`;

const HelperText = styled.div`
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 1.5;
  color: #17171B;
  margin-top: ${convertToVwMob(8)};
`;

const ErrorText = styled.div`
  width: 100%;
  margin-top: ${convertToVwMob(16)};
  font-size: ${convertToVwMob(16)};
  font-weight: 400;
  line-height: 1.4;
  color: #d33;
`;

const PasswordErrorText = styled.div`
  color: var(--Red-10, #F00);
  font-family: Pretendard;
  font-size: ${convertToVwMob(16)};
  font-style: normal;
  font-weight: 400;
  line-height: 1.4;
  margin-top: ${convertToVwMob(8)};
`;

const EmailCheckText = styled.div<{ $status: 'checking' | 'available' | 'duplicate' | 'error' | 'idle' }>`
  margin-top: ${convertToVwMob(8)};
  font-size: ${convertToVwMob(16)};
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(8)};
  color: ${({ $status }) => ($status === 'available' ? '#0B6773' : $status === 'checking' ? '#676767' : '#d33')};
`;

const EmailCheckSpinner = styled.span`
  width: ${convertToVwMob(14)};
  height: ${convertToVwMob(14)};
  border: 2px solid #d9d9d9;
  border-top-color: #676767;
  border-radius: 50%;
  animation: signupEmailSpin 0.8s linear infinite;

  @keyframes signupEmailSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;


const InfoLabel = styled.div`
  font-size: ${convertToVwMob(20)};
  color: #676767;
  font-weight: 400;
  line-height: 1.5;
`;

const InfoValue = styled.div`
  font-size: ${convertToVwMob(20)};
  color: #17171B;
  font-weight: 400;
  line-height: 1.5;
`;



const ChevronIcon = styled.div<{ isOpen: boolean }>`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9L12 15L18 9' stroke='%23191919' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  transition: transform 0.2s;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
`;




const KeyboardButtonWrapper = styled.div<{ isKeyboardVisible: boolean }>`
  width: ${({ isKeyboardVisible }) => (isKeyboardVisible ? '100vw' : '100%')};
  max-width: ${({ isKeyboardVisible }) => (isKeyboardVisible ? 'none' : convertToVwMob(353))};
  margin: 0 auto;
  padding-bottom: ${({ isKeyboardVisible }) => (isKeyboardVisible ? '0' : convertToVwMob(30))};
  flex-shrink: 0;
  z-index: 1000;
  position: ${({ isKeyboardVisible }) => (isKeyboardVisible ? 'fixed' : 'relative')};
  left: ${({ isKeyboardVisible }) => (isKeyboardVisible ? '0' : 'auto')};
  right: ${({ isKeyboardVisible }) => (isKeyboardVisible ? '0' : 'auto')};
  bottom: ${({ isKeyboardVisible }) => (isKeyboardVisible ? '0' : 'auto')};
`;

const NextButton = styled.button<{ isKeyboardMode?: boolean }>`
  width: 100%;
  height: ${convertToVwMob(60)};
  padding: ${convertToVwMob(10)} ${convertToVwMob(24)};
  background: ${({ disabled }) => (disabled ? '#E5E5E5' : 'var(--PrimaryBlue-80, #026478)')};
  color: ${({ disabled }) => (disabled ? '#BCBCBC' : 'white')};
  border: none;
  border-radius: ${({ isKeyboardMode }) => (isKeyboardMode ? '0' : '12px')};
  font-size: ${convertToVwMob(20)};
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  line-height: 1.5;
  display: flex;
  align-items: center;
  justify-content: center;
`;





const SelectFieldWrapper = styled.div`
  width: 100%;
  padding: ${convertToVwMob(12)} 0;
  border-bottom: 1px solid #BCBCBC;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const SelectFieldLabel = styled.div<{ hasValue: boolean }>`
  color: ${({ hasValue }) => (hasValue ? 'var(--GreyScale-70, #676767)' : '#676767')};
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`;

const StyledBottomSheetContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  margin-bottom: ${convertToVwMob(16)};
`;

const RadioOptionItem = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(10)};
  cursor: pointer;
  padding: ${convertToVwMob(6)} 0;
`;

const BottomSheetButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(60)};
  background: ${({ disabled }) => (disabled ? '#E5E5E5' : '#026478')};
  color: ${({ disabled }) => (disabled ? '#BCBCBC' : 'white')};
  border: none;
  border-radius: ${convertToVwMob(12)};
  font-size: ${convertToVwMob(18)};
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

const Step4Container = styled.div<{ $enableScroll: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  height: 100dvh;
  padding: 0 ${convertToVwMob(24)} calc(${convertToVwMob(22)} + env(safe-area-inset-bottom));
  overflow-y: ${({ $enableScroll }) => ($enableScroll ? 'auto' : 'hidden')};
  -webkit-overflow-scrolling: touch;
`;

const Step4LogoSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: ${convertToVwMob(52)} 0 ${convertToVwMob(14)};
  flex-shrink: 0;
`;

const WelcomeLogo = styled.img`
  width: ${convertToVwMob(168)};
  height: ${convertToVwMob(66)};
  object-fit: contain;
`;

const Step4Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  overflow: visible;
`;

const WelcomeTitle = styled.h1`
  font-size: ${convertToVwMob(32)};
  font-weight: 400;
  color: #17171B;
  margin: ${convertToVwMob(26)} 0 0 0;
  text-align: center;
  line-height: 140%;
`;

const WelcomeSubText = styled.p`
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  color: #17171B;
  text-align: center;
  line-height: 150%;
  margin: ${convertToVwMob(24)} 0 0 0;
  word-break: keep-all;
`;

const WelcomeProfileSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(24)};
  margin-top: ${convertToVwMob(24)};
`;

const WelcomeProfileCircle = styled.div`
  width: ${convertToVwMob(124)};
  height: ${convertToVwMob(124)};
  border-radius: ${convertToVwMob(48)};
  background: #E8F8FC;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const WelcomeProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${convertToVwMob(4)};
`;

const WelcomeProfileEmail = styled.div`
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  color: #17171B;
  text-align: center;
  line-height: 150%;
`;

const WelcomeProfileName = styled.div`
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  color: #17171B;
  text-align: center;
  line-height: 150%;
`;

const Step4ButtonWrapper = styled.div`
  width: 100%;
  max-width: ${convertToVwMob(353)};
  margin: 0 auto;
  margin-top: ${convertToVwMob(72)};
  padding-top: 0;
  padding-bottom: 0;
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(14)};
  flex-shrink: 0;
`;

const WelcomeButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(60)};
  background: #026478;
  color: white;
  border: none;
  border-radius: ${convertToVwMob(12)};
  font-size: ${convertToVwMob(24)};
  font-weight: 500;
  line-height: ${convertToVwMob(30)};
  cursor: pointer;
`;

const LaterButton = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: #929292;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;
  cursor: pointer;
  padding: ${convertToVwMob(12)} 0;
  text-decoration: underline;
`;

export default SignupMob;
